/**
 * «Жишээ тооцоо» — de-invoice-service/src/logics/late-fee/calculate.ts-ийн calculateLateFee-ийг яг тэр дарааллаар давтана:
 * penaltyDays = daysPastDue - grace (<=0 бол 0) → төрлөөр fee → maxPenaltyAmount → maxPenaltyRate (нийт дүнгийн %) →
 * minimumPenaltyAmount (fee-ээс их бол fee = min) → 2 орны бүхэлчлэл.
 * ESCALATING нь settings маягтад байхгүй escalationTiers-ээс хамаарах тул энд бодохгүй (null буцаана).
 */
export const EXAMPLE_FEE_TYPES = ["DAILY_PERCENTAGE", "MONTHLY_PERCENTAGE", "COMPOUND_DAILY", "FLAT_FEE"] as const;

export type ExampleInput = {
  feeCalculationType: string | null;
  dailyPenaltyRate: number | string;
  monthlyPenaltyRate: number | string;
  flatFeeAmount: number | string;
  gracePeriodDays: number | string;
  maxPenaltyRate: number | string;
  maxPenaltyAmount: number | string;
  minimumPenaltyAmount: number | string;
};

export type LateFeeExample = {
  daysPastDue: number;
  graceDays: number;
  penaltyDays: number;
  /** Нэг өдрийн (COMPOUND бол эхний өдрийн) алданги; FLAT_FEE-д байхгүй. */
  perDayFee: number | null;
  capRateAmount: number | null;
  capRatePercent: number | null;
  capAmount: number | null;
  minAmount: number | null;
  fee: number;
};

const isSet = (v: number | string | null | undefined) => v !== "" && v !== null && v !== undefined;
const num = (v: number | string | null | undefined) => Number(v) || 0;

export function computeLateFeeExample(config: ExampleInput, invoiceAmount: number, daysPastDue: number): LateFeeExample | null {
  const type = config.feeCalculationType;
  if (!type || !(EXAMPLE_FEE_TYPES as readonly string[]).includes(type)) return null;

  const graceDays = isSet(config.gracePeriodDays) ? num(config.gracePeriodDays) : 0;
  const penaltyDays = daysPastDue - graceDays;
  const remaining = invoiceAmount;
  const total = invoiceAmount;

  let perDayFee: number | null = null;
  if (type === "DAILY_PERCENTAGE" || type === "COMPOUND_DAILY") perDayFee = remaining * (num(config.dailyPenaltyRate) / 100);
  if (type === "MONTHLY_PERCENTAGE") perDayFee = remaining * (num(config.monthlyPenaltyRate) / 100) * (1 / 30);

  const capAmount = isSet(config.maxPenaltyAmount) ? num(config.maxPenaltyAmount) : null;
  const capRatePercent = isSet(config.maxPenaltyRate) ? num(config.maxPenaltyRate) : null;
  const capRateAmount = capRatePercent === null ? null : (total * capRatePercent) / 100;
  const minAmount = isSet(config.minimumPenaltyAmount) ? num(config.minimumPenaltyAmount) : null;

  const base: LateFeeExample = {
    daysPastDue,
    graceDays,
    penaltyDays,
    perDayFee,
    capRateAmount,
    capRatePercent,
    capAmount,
    minAmount,
    fee: 0,
  };
  if (penaltyDays <= 0) return base;

  let fee = 0;
  switch (type) {
    case "DAILY_PERCENTAGE":
      fee = remaining * (num(config.dailyPenaltyRate) / 100) * penaltyDays;
      break;
    case "MONTHLY_PERCENTAGE":
      fee = remaining * (num(config.monthlyPenaltyRate) / 100) * (penaltyDays / 30);
      break;
    case "COMPOUND_DAILY":
      fee = remaining * (Math.pow(1 + num(config.dailyPenaltyRate) / 100, penaltyDays) - 1);
      break;
    case "FLAT_FEE":
      fee = num(config.flatFeeAmount);
      break;
  }

  if (capAmount !== null) fee = Math.min(fee, capAmount);
  if (capRateAmount !== null) fee = Math.min(fee, capRateAmount);
  if (minAmount !== null && fee < minAmount) fee = minAmount;

  return { ...base, fee: Math.round(fee * 100) / 100 };
}
